import { createContext, type ReactNode, useEffect, useMemo } from 'react';

import { DEFAULT_I18N_LANGUAGE } from './constants';
import { getTranslationValue } from './getTranslationValue';
import { interpolateTranslation } from './interpolateTranslation';
import { clearRuntimeI18nState, getRuntimeI18nState, setRuntimeI18nState } from './translate';
import type { I18nKey, I18nLanguage, I18nNamespace, I18nResources, I18nTranslateFunction } from './types';

type I18nContextValue = {
  language: I18nLanguage;
  t: I18nTranslateFunction<I18nKey>;
};

const defaultContextValue: I18nContextValue = {
  language: DEFAULT_I18N_LANGUAGE,
  t: (key, options) => options?.defaultValue ?? key,
};

export const I18nContext = createContext<I18nContextValue>(defaultContextValue);

export const I18nProvider = <Namespaces extends I18nNamespace>({
  children,
  resources,
  language: languageProp,
}: {
  children: ReactNode;
  resources: I18nResources<Namespaces>;
  language?: I18nLanguage;
}) => {
  const language = languageProp ?? DEFAULT_I18N_LANGUAGE;

  const t = useMemo<I18nTranslateFunction<I18nKey>>(() => {
    return (key, options) => {
      const languageResource = resources[language];
      const fallbackLanguageResource = resources[DEFAULT_I18N_LANGUAGE];

      const translatedValue =
        getTranslationValue(languageResource, key) ?? getTranslationValue(fallbackLanguageResource, key);

      const rawValue = translatedValue ?? options?.defaultValue ?? key;

      return interpolateTranslation(rawValue, options);
    };
  }, [language, resources]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      t,
    }),
    [language, t]
  );

  useEffect(() => {
    setRuntimeI18nState(value);

    return () => {
      if (getRuntimeI18nState() === value) {
        clearRuntimeI18nState();
      }
    };
  }, [value]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};
